/**
 * 节点容器：负责选中态 / 拖拽 / 左右锚点 / 工具栏 hover 等通用能力。
 *
 * 子组件通过 children 渲染节点本体内容；如果需要"工具栏"传 toolbar。
 */
import { useEffect, useRef, useState } from 'react';
import {
  Trash2,
  Type as TypeIcon,
  Image as ImageIcon,
  Video as VideoIcon,
  Wand2 as Wand2Icon,
  Layers as LayersIcon,
  Sparkles as SparklesIcon,
  Bot as BotIcon,
  Palette,
  Music as MusicIcon,
} from 'lucide-react';
import type { CanvasNode } from '../types';
import { boardActions, getBoardState, useBoard } from '../state/boardStore';
import { nid } from '../utils';
import { useContextMenu } from '../ContextMenu';
import { ContextMenu } from '../ContextMenu';
import { ConnectionCreateMenu } from '../ConnectionCreateMenu';
import { NodeInfoDialog } from '../NodeInfoDialog';
import toast from '../../../components/Toast';
import { canConnectNodes } from '../connectionRules';
import { deleteNodesWithUnitGuard, expandIdsWithUnits } from '../generators/agentUnit';
import {
  addImageToGroup,
  aggregateSelectedImageNodes,
  mergeImageGroups,
} from '../generators/groupOps';
import {
  addVideoToGroup,
  aggregateSelectedVideoNodes,
  mergeVideoGroups,
} from '../generators/videoGroupOps';

interface NodeWrapperProps {
  node: CanvasNode;
  selected: boolean;
  highlighted: boolean;
  children: React.ReactNode;
  toolbar?: React.ReactNode;
  /** 选中节点时在卡片下方展开的面板（提示词编辑等大块表单，对齐 infinite-canvas 布局） */
  panel?: React.ReactNode;
  /** 卡片手动放大时内容层撑满剩余高度（内容根需配合 flex-1；默认内容自然高度） */
  stretchContent?: boolean;
}

// 卡片本体拖拽时放行的交互元素（这些目标的 pointerdown 不触发拖动/选区）
const INTERACTIVE_SELECTOR =
  'button, input, textarea, select, a, label, video, audio, canvas, [contenteditable], [data-no-drag]';

// 按下位移超过该值（屏幕像素）才视为拖拽并捕获指针
const DRAG_START_THRESHOLD = 3;

// 节点类型 → 视觉配置。
// 配色按语义收敛为 3 组（避免 7 种节点 7 个色相造成画布彩虹噪点）：
//   数据载体（text）= 中性灰蓝；处理节点（generate_* / llm）= 蓝；产物节点（image / video / image_group）= 绿。
// 颜色只出现在头部 24px 图标色块与锚点 hover 态，卡片本体保持中性。
interface NodeTypeVisual {
  label: string;
  icon: React.ReactNode;
  /** 语义主色：图标色块底（低透明度）+ 图标前景 + 锚点 hover */
  accent: string;
}

function getNodeTypeVisual(type: string): NodeTypeVisual {
  switch (type) {
    case 'text':
      return { label: '文本', icon: <TypeIcon className="h-3.5 w-3.5" />, accent: '#64748b' };
    case 'image':
      return { label: '图片', icon: <ImageIcon className="h-3.5 w-3.5" />, accent: '#10b981' };
    case 'video':
      return { label: '视频', icon: <VideoIcon className="h-3.5 w-3.5" />, accent: '#10b981' };
    case 'audio':
      return { label: '声音', icon: <MusicIcon className="h-3.5 w-3.5" />, accent: '#ec4899' };
    case 'generate_image':
      return { label: '生图', icon: <Wand2Icon className="h-3.5 w-3.5" />, accent: '#2563eb' };
    case 'generate_video':
      return { label: '生视频', icon: <VideoIcon className="h-3.5 w-3.5" />, accent: '#2563eb' };
    case 'image_group':
      return { label: '图片组', icon: <LayersIcon className="h-3.5 w-3.5" />, accent: '#10b981' };
    case 'video_group':
      return { label: '视频组', icon: <LayersIcon className="h-3.5 w-3.5" />, accent: '#10b981' };
    case 'llm':
      return { label: 'LLM', icon: <SparklesIcon className="h-3.5 w-3.5" />, accent: '#2563eb' };
    case 'agent':
      return { label: '智能体', icon: <BotIcon className="h-3.5 w-3.5" />, accent: '#4f46e5' };
    case 'style_card':
      return { label: '风格卡片', icon: <Palette className="h-3.5 w-3.5" />, accent: '#a855f7' };
    default:
      return { label: type, icon: null, accent: '#64748b' };
  }
}

export function NodeWrapper({
  node,
  selected,
  highlighted,
  children,
  toolbar,
  panel,
  stretchContent = false,
}: NodeWrapperProps) {
  // 参考图点选模式：本节点相对选择源的三态（source / connected / available / disabled）
  const referencePicker = useBoard((s) => s.referencePicker);
  const pickerState = (() => {
    if (!referencePicker) return null;
    if (referencePicker.sourceNodeId === node.id) return 'source' as const;
    const cur = getBoardState();
    const source = cur.nodes.find((n) => n.id === referencePicker.sourceNodeId);
    if (!source) return null;
    const connected = cur.edges.some(
      (e) => e.sourceNodeId === source.id && e.targetNodeId === node.id
    );
    return connected ? ('connected' as const) : canConnectNodes(source, node).ok ? ('available' as const) : ('disabled' as const);
  })();
  const draggingRef = useRef<{
    startX: number;
    startY: number;
    pointerId: number;
    /** true = 拖拽未正式启动，等待位移超过阈值后再捕获指针 */
    capturePending: boolean;
    nodeIds: string[];
    initial: Record<string, { x: number; y: number }>;
    /** 累计画布位移：每段屏幕增量按发生时刻的 zoom 换算累加。
     *  拖拽期间滚轮缩放会改变 zoom，不能用「总屏幕位移 / 当前 zoom」重算 */
    canvasDx: number;
    canvasDy: number;
    /** rAF 合帧：最新事件坐标（帧回调消费） */
    lastX: number;
    lastY: number;
    /** 待执行的动画帧句柄，null = 空闲 */
    frame: number | null;
  } | null>(null);
  // 参考图点选：pointerup 记录本次按下是否发生过真实拖拽（阈值已越过），
  // 拖拽释放后浏览器仍会派发 click，若不抑制会误触连线的建立/断开。
  const pickerClickSuppressRef = useRef(false);
  const { menuState, onContextMenu, closeMenu } = useContextMenu();
  const [connectionMenuState, setConnectionMenuState] = useState<{
    x: number;
    y: number;
    sourceNodeId: string;
  } | null>(null);
  // 节点改名（双击标题）与信息弹窗
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [infoOpen, setInfoOpen] = useState(false);
  // 四角 resize：拖动改变节点宽度（图片/视频类保持内容比例，其余同步高度）
  const resizeRef = useRef<{
    corner: 'nw' | 'ne' | 'sw' | 'se';
    startX: number;
    startY: number;
    rect: { x: number; y: number; width: number; height: number };
  } | null>(null);

  const lockRatio = (() => {
    const v = (node.data as Record<string, unknown>).lockRatio;
    // 图片/视频类默认锁比例，其余默认自由
    if (node.type === 'image' || node.type === 'video') return v !== false;
    return v === true;
  })();

  const startResize = (corner: 'nw' | 'ne' | 'sw' | 'se') => (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    resizeRef.current = {
      corner,
      startX: e.clientX,
      startY: e.clientY,
      rect: { x: node.x, y: node.y, width: node.width, height: node.height },
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onResizeMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = resizeRef.current;
    if (!r) return;
    const zoom = getBoardState().viewport.zoom;
    const dx = (e.clientX - r.startX) / zoom;
    const dy = (e.clientY - r.startY) / zoom;
    const MIN_W = 200;
    const MIN_H = 120;
    let { x, y, width, height } = r.rect;
    if (r.corner === 'se') {
      width = Math.max(MIN_W, r.rect.width + dx);
      height = Math.max(MIN_H, r.rect.height + dy);
    } else if (r.corner === 'ne') {
      width = Math.max(MIN_W, r.rect.width + dx);
      const bottom = r.rect.y + r.rect.height;
      height = Math.max(MIN_H, r.rect.height - dy);
      y = bottom - height;
    } else if (r.corner === 'sw') {
      const right = r.rect.x + r.rect.width;
      width = Math.max(MIN_W, r.rect.width - dx);
      x = right - width;
      height = Math.max(MIN_H, r.rect.height + dy);
    } else {
      const right = r.rect.x + r.rect.width;
      const bottom = r.rect.y + r.rect.height;
      width = Math.max(MIN_W, r.rect.width - dx);
      height = Math.max(MIN_H, r.rect.height - dy);
      x = right - width;
      y = bottom - height;
    }
    // 锁比例：以宽度为基准等比缩放，锚定对侧角
    if (lockRatio && r.rect.width > 0) {
      const ratio = r.rect.height / r.rect.width;
      height = Math.round(width * ratio);
      if (r.corner === 'nw' || r.corner === 'ne') y = r.rect.y + r.rect.height - height;
    }
    boardActions.updateNode(node.id, { x, y, width, height });
  };

  const endResize = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!resizeRef.current) return;
    resizeRef.current = null;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    boardActions.commitNodeMutation();
  };

  // 内容高度自适应：观察内容层（不受卡片 minHeight 钳制，量到的是自然高度），
  // 双向同步回 store，保证卡片高度始终贴合内容、连线锚点与视觉中心对齐。
  // 旧实现观察整卡：内容变矮时 minHeight 把卡片钉在存储高度，多余高度永远收不回
  // （生视频节点默认 270 但内容只有约 190，底部留 ~80px 空白的根因）。
  // 用 offsetHeight（不受视口缩放影响），>2px 才写入避免抖动。
  const contentRef = useRef<HTMLDivElement | null>(null);
  const measuredHeightRef = useRef<number>(node.height);
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    // 只读预览：不做尺寸回写，避免无谓的观察与写操作
    if (getBoardState().readOnly) return;
    measuredHeightRef.current = node.height;
    const observer = new ResizeObserver(() => {
      // 用户拖角调整尺寸期间不回写，避免与拖拽的高度写入互相打架
      if (resizeRef.current) return;
      // +4px = 卡片上下 border-2 的盒模型差，保证 node.height 等于整卡渲染高度
      const height = el.offsetHeight + 4;
      if (Math.abs(height - measuredHeightRef.current) > 2) {
        measuredHeightRef.current = height;
        boardActions.updateNode(node.id, { height });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id]);

  // ---------------- 卡片本体拖拽（交互元素区域放行） ----------------
  const onCardPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // 画布层的框选/平移一律不触发（节点自身的 pointerdown 全部截断）
    e.stopPropagation();
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest(INTERACTIVE_SELECTOR)) return;

    // 选中：Shift 追加；否则替换为单选
    const cur = getBoardState();
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      const nextNodeIds = cur.selection.nodeIds.includes(node.id)
        ? cur.selection.nodeIds.filter((id) => id !== node.id)
        : [...cur.selection.nodeIds, node.id];
      boardActions.setSelection({ nodeIds: nextNodeIds, edgeIds: [] });
    } else if (!cur.selection.nodeIds.includes(node.id)) {
      boardActions.setSelection({ nodeIds: [node.id], edgeIds: [] });
    }

    // 拖拽多选时，所有选中节点一起移动；智能体批次成员触发冻结单元
    // 整组联动（全单元 + 外框一起移动，见 agentUnit）
    const after = getBoardState();
    const selectedIds = after.selection.nodeIds.includes(node.id)
      ? after.selection.nodeIds
      : [node.id];
    const nodeIds = expandIdsWithUnits(selectedIds, after.nodes);
    const initial: Record<string, { x: number; y: number }> = {};
    after.nodes.forEach((n) => {
      if (nodeIds.includes(n.id)) initial[n.id] = { x: n.x, y: n.y };
    });
    draggingRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      pointerId: e.pointerId,
      capturePending: true,
      nodeIds,
      initial,
      canvasDx: 0,
      canvasDy: 0,
      lastX: e.clientX,
      lastY: e.clientY,
      frame: null,
    };
    pickerClickSuppressRef.current = false;
    // 不在此处 setPointerCapture：立即捕获会把后续 click/dblclick 重定向到卡片根，
    // 内容区的双击编辑（文本节点 / 标题重命名）将收不到事件。
    // 真正的捕获推迟到 onCardPointerMove 位移超阈值时。
  };

  /**
   * 应用一帧拖拽位移：对齐吸附计算 + 位置写入。
   * 从 rAF 回调消费（合帧），pointerup 时同步冲刷最后一帧。
   * 位移来自 canvasDx/canvasDy（每段增量按发生时 zoom 换算的累计值），
   * 拖拽期间滚轮缩放不会造成节点跳变。
   */
  const applyDragFrame = () => {
    const drag = draggingRef.current;
    if (!drag || drag.frame == null) return;
    drag.frame = null;

    let dx = drag.canvasDx;
    let dy = drag.canvasDy;

    // 单节点拖拽：与其他节点的 左/中X/右、上/中Y/下 对齐吸附并显示辅助线
    if (drag.nodeIds.length === 1) {
      const init = drag.initial[drag.nodeIds[0]];
      if (init) {
        const zoom = getBoardState().viewport.zoom;
        const threshold = 6 / zoom;
        const movingX = [init.x + dx, init.x + dx + node.width / 2, init.x + dx + node.width];
        const movingY = [init.y + dy, init.y + dy + node.height / 2, init.y + dy + node.height];
        let bestX: { delta: number; guide: number } | null = null;
        let bestY: { delta: number; guide: number } | null = null;
        for (const other of getBoardState().nodes) {
          if (other.id === node.id) continue;
          for (const target of [other.x, other.x + other.width / 2, other.x + other.width]) {
            for (const m of movingX) {
              const delta = target - m;
              if (Math.abs(delta) <= threshold && (!bestX || Math.abs(delta) < Math.abs(bestX.delta))) {
                bestX = { delta, guide: target };
              }
            }
          }
          for (const target of [other.y, other.y + other.height / 2, other.y + other.height]) {
            for (const m of movingY) {
              const delta = target - m;
              if (Math.abs(delta) <= threshold && (!bestY || Math.abs(delta) < Math.abs(bestY.delta))) {
                bestY = { delta, guide: target };
              }
            }
          }
        }
        if (bestX) dx += bestX.delta;
        if (bestY) dy += bestY.delta;
        boardActions.setAlignmentGuides(
          bestX || bestY
            ? { v: bestX ? [bestX.guide] : [], h: bestY ? [bestY.guide] : [] }
            : null
        );
      }
    }

    drag.nodeIds.forEach((id) => {
      const init = drag.initial[id];
      if (!init) return;
      boardActions.updateNode(id, { x: init.x + dx, y: init.y + dy });
    });
  };

  const onCardPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = draggingRef.current;
    if (!drag) return;

    // 增量累计：本段屏幕位移按当前 zoom 换算成画布位移累加，
    // 拖拽中途缩放（滚轮）也不会重算历史位移造成跳变
    const zoom = getBoardState().viewport.zoom;
    drag.canvasDx += (e.clientX - drag.lastX) / zoom;
    drag.canvasDy += (e.clientY - drag.lastY) / zoom;
    drag.lastX = e.clientX;
    drag.lastY = e.clientY;

    // 位移超过阈值才正式启动拖拽并捕获指针（屏幕像素判定）。
    // setPointerCapture 必须在事件处理器内同步执行，不能推迟到 rAF
    if (drag.capturePending) {
      if (
        Math.abs(e.clientX - drag.startX) < DRAG_START_THRESHOLD &&
        Math.abs(e.clientY - drag.startY) < DRAG_START_THRESHOLD
      ) {
        return;
      }
      drag.capturePending = false;
      (e.currentTarget as HTMLElement).setPointerCapture(drag.pointerId);
    }

    // rAF 合帧：pointermove 可能远高于刷新率，一帧只应用一次位置更新，
    // 避免"每次事件 = 整树重渲染"把拖拽拖出可感知延迟
    if (drag.frame == null) {
      drag.frame = requestAnimationFrame(applyDragFrame);
    }
  };

  const onCardPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = draggingRef.current;
    if (!drag) return;
    // 冲刷未执行的最后一帧：先累计 pointerup 带来的最后一段增量，
    // 落定最终位置后再提交历史
    const zoom = getBoardState().viewport.zoom;
    drag.canvasDx += (e.clientX - drag.lastX) / zoom;
    drag.canvasDy += (e.clientY - drag.lastY) / zoom;
    drag.lastX = e.clientX;
    drag.lastY = e.clientY;
    if (drag.frame != null) {
      cancelAnimationFrame(drag.frame);
      applyDragFrame();
    }
    const moved =
      Math.abs(e.clientX - drag.startX) > 1 ||
      Math.abs(e.clientY - drag.startY) > 1;
    // capturePending=false 表示拖拽已正式启动（超过阈值），紧随其后的 click 是拖拽释放
    pickerClickSuppressRef.current = !drag.capturePending;
    draggingRef.current = null;
    boardActions.setAlignmentGuides(null);
    try {
      // 未超阈值的点击不会建立指针捕获，跳过释放
      if ((e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      }
    } catch {
      /* noop */
    }
    if (moved) {
      boardActions.commitNodeMutation();
    }
  };

  /** pointercancel：触摸拖拽被浏览器接管（手势识别/手掌误触）时中断拖拽，
   *  只清理状态不提交历史，避免 rAF 残留与状态泄漏 */
  const onCardPointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = draggingRef.current;
    if (!drag) return;
    if (drag.frame != null) cancelAnimationFrame(drag.frame);
    draggingRef.current = null;
    boardActions.setAlignmentGuides(null);
    try {
      if ((e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      }
    } catch {
      /* noop */
    }
  };

  // 锚点连线：从右锚点拖到目标节点本体上建立连线
  const startConnection = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const startX = e.clientX;
    const startY = e.clientY;
    let isDragging = false;

    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;cursor:crosshair;z-index:99999;background:rgba(0,0,0,0.05);';
    document.body.appendChild(overlay);

    // 临时连线预览
    const tempLine = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    tempLine.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:100000;';
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('stroke', '#3b82f6');
    path.setAttribute('stroke-width', '3');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke-dasharray', '5,5');
    tempLine.appendChild(path);
    document.body.appendChild(tempLine);

    const cleanup = () => {
      overlay.removeEventListener('pointermove', onMove);
      overlay.removeEventListener('pointerup', onUp);
      overlay.removeEventListener('pointercancel', onCancel);
      overlay.remove();
      tempLine.remove();
    };

    const onMove = (ev: PointerEvent) => {
      const dx = Math.abs(ev.clientX - startX);
      const dy = Math.abs(ev.clientY - startY);
      if (dx > 3 || dy > 3) isDragging = true;

      // 更新临时连线
      const d = `M ${startX} ${startY} L ${ev.clientX} ${ev.clientY}`;
      path.setAttribute('d', d);
    };

    const onUp = (ev: PointerEvent) => {
      if (!isDragging) {
        cleanup();
        return;
      }

      // 临时隐藏overlay和tempLine以便获取下方元素
      overlay.style.display = 'none';
      tempLine.style.display = 'none';

      const target = (document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement) || null;
      const targetCard = target?.closest('[data-canvas-node-id]');
      const targetNodeId = targetCard?.getAttribute('data-canvas-node-id');

      if (targetNodeId && targetNodeId !== node.id) {
        const cur = getBoardState();
        const targetNode = cur.nodes.find((n) => n.id === targetNodeId);
        if (!targetNode) {
          cleanup();
          return;
        }
        const connectCheck = canConnectNodes(node, targetNode);
        if (!connectCheck.ok) {
          toast.error(connectCheck.reason || '无法连接该节点');
          cleanup();
          return;
        }
        // 图片连图片：手动手势不落边，直接合并为图片组（与多选聚合同一原子操作）
        if (node.type === 'image' && targetNode.type === 'image') {
          aggregateSelectedImageNodes([node.id, targetNodeId]);
          cleanup();
          return;
        }
        // 图片拖到图片组：加入该组（图片被吸收，不落边）
        if (node.type === 'image' && targetNode.type === 'image_group') {
          addImageToGroup(node.id, targetNodeId);
          cleanup();
          return;
        }
        // 图片组拖到图片组：合并（源组消失，不落边）
        if (node.type === 'image_group' && targetNode.type === 'image_group') {
          mergeImageGroups(node.id, targetNodeId);
          cleanup();
          return;
        }
        // 视频连视频：手势不落边，直接合并为视频组
        if (node.type === 'video' && targetNode.type === 'video') {
          aggregateSelectedVideoNodes([node.id, targetNodeId]);
          cleanup();
          return;
        }
        // 视频拖到视频组：加入该组（视频被吸收，不落边）
        if (node.type === 'video' && targetNode.type === 'video_group') {
          addVideoToGroup(node.id, targetNodeId);
          cleanup();
          return;
        }
        // 视频组拖到视频组：合并（源组消失，不落边）
        if (node.type === 'video_group' && targetNode.type === 'video_group') {
          mergeVideoGroups(node.id, targetNodeId);
          cleanup();
          return;
        }
        boardActions.addEdge({
          id: nid(),
          sourceNodeId: node.id,
          targetNodeId,
          sourceHandle: 'right',
          targetHandle: 'left',
        });
        cleanup();
      } else {
        // 拖到空白处：弹出创建菜单
        cleanup();
        setConnectionMenuState({ x: ev.clientX, y: ev.clientY, sourceNodeId: node.id });
      }
    };

    const onCancel = () => {
      cleanup();
    };

    overlay.addEventListener('pointermove', onMove);
    overlay.addEventListener('pointerup', onUp);
    overlay.addEventListener('pointercancel', onCancel);
  };

  const visual = getNodeTypeVisual(node.type);

  // ---------------- 参考图点选：点击节点即建立/断开与源节点的连线 ----------------
  const handlePickerClickCapture = (e: React.MouseEvent) => {
    if (!referencePicker || pickerState === 'source') return;
    // 拖拽释放产生的 click 不是点选意图，跳过一次并复位标记
    if (pickerClickSuppressRef.current) {
      pickerClickSuppressRef.current = false;
      return;
    }
    const cur = getBoardState();
    const source = cur.nodes.find((n) => n.id === referencePicker.sourceNodeId);
    if (!source) {
      boardActions.cancelReferencePick();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (pickerState === 'disabled') {
      toast.error(canConnectNodes(source, node).reason || '无法连接该节点');
      return;
    }
    // 图片连图片：点选手势不落边，直接合并为图片组（与多选聚合同一原子操作）
    if (source.type === 'image' && node.type === 'image' && source.id !== node.id) {
      aggregateSelectedImageNodes([source.id, node.id]);
      return;
    }
    // 图片点到图片组：加入该组；图片组点到图片组：合并（均不落边）
    if (source.type === 'image' && node.type === 'image_group' && source.id !== node.id) {
      addImageToGroup(source.id, node.id);
      return;
    }
    if (source.type === 'image_group' && node.type === 'image_group' && source.id !== node.id) {
      mergeImageGroups(source.id, node.id);
      return;
    }
    // 视频连视频：点选不落边，直接合并为视频组
    if (source.type === 'video' && node.type === 'video' && source.id !== node.id) {
      aggregateSelectedVideoNodes([source.id, node.id]);
      return;
    }
    // 视频点到视频组：加入该组；视频组点到视频组：合并（均不落边）
    if (source.type === 'video' && node.type === 'video_group' && source.id !== node.id) {
      addVideoToGroup(source.id, node.id);
      return;
    }
    if (source.type === 'video_group' && node.type === 'video_group' && source.id !== node.id) {
      mergeVideoGroups(source.id, node.id);
      return;
    }
    const edge = cur.edges.find(
      (ed) => ed.sourceNodeId === source.id && ed.targetNodeId === node.id
    );
    if (edge) {
      const { blocked } = boardActions.removeEdges([edge.id]);
      if (blocked > 0) {
        toast.error('智能体批次的血缘连线不可手动断开，如需删除请整体删除该批次');
      } else {
        toast.success('已移除连线');
      }
    } else {
      boardActions.addEdge({
        id: nid(),
        sourceNodeId: source.id,
        targetNodeId: node.id,
        sourceHandle: 'right',
        targetHandle: 'left',
      });
      toast.success('已连接');
    }
  };

  // ---------------- 节点改名（双击标题，存 data._title） ----------------
  const nodeTitle = (() => {
    const t = (node.data as Record<string, unknown>)._title;
    return typeof t === 'string' && t.trim() ? t.trim() : visual.label;
  })();

  const startTitleEditing = () => {
    // 自定义名时从当前名编辑，否则清空便于输入新名
    const t = (node.data as Record<string, unknown>)._title;
    setTitleDraft(typeof t === 'string' ? t : '');
    setEditingTitle(true);
  };

  const commitTitle = () => {
    setEditingTitle(false);
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== visual.label) {
      boardActions.updateNodeData(node.id, { _title: trimmed });
      boardActions.commitNodeMutation();
    } else {
      // 清空或与类型名相同：移除自定义名
      const t = (node.data as Record<string, unknown>)._title;
      if (typeof t === 'string') {
        boardActions.updateNodeData(node.id, { _title: null });
        boardActions.commitNodeMutation();
      }
    }
  };

  return (
    <div
      data-canvas-node-id={node.id}
      className={`canvas-node-card group absolute select-none rounded-3xl bg-white transition-shadow duration-200 ${
        stretchContent ? 'flex flex-col ' : ''
      }${
        pickerState === 'connected'
          ? 'border-2 border-emerald-400 shadow-[0_0_0_1px_rgba(16,185,129,0.35)]'
          : pickerState === 'available'
            ? 'border-2 border-blue-400 shadow-[0_0_0_4px_rgba(59,130,246,0.18)] cursor-pointer'
            : pickerState === 'disabled'
              ? 'border-2 border-slate-200 opacity-40 saturate-0'
              : pickerState === 'source'
                ? 'border-2 border-amber-400 shadow-[0_0_0_1px_rgba(251,191,36,0.4)]'
                : selected
                  ? 'border-2 border-blue-500 shadow-[0_0_0_1px_rgba(59,130,246,0.45),0_18px_48px_rgba(0,0,0,0.14)]'
                  : highlighted
                    ? 'border-2 border-blue-300 shadow-[0_0_0_1px_rgba(59,130,246,0.25),0_18px_48px_rgba(0,0,0,0.1)]'
                    : 'border-2 border-slate-200 shadow-[0_1px_3px_rgba(15,23,42,0.06)] hover:border-slate-300 hover:shadow-[0_18px_48px_rgba(0,0,0,0.12)]'
      }`}
      style={
        {
          left: node.x,
          top: node.y,
          width: node.width,
          minHeight: node.height,
          zIndex: selected ? 999 : node.zIndex,
          '--node-accent': visual.accent,
        } as React.CSSProperties
      }
      onPointerDown={onCardPointerDown}
      onPointerMove={onCardPointerMove}
      onPointerUp={onCardPointerUp}
      onPointerCancel={onCardPointerCancel}
      onClickCapture={handlePickerClickCapture}
      onContextMenu={(e) => onContextMenu(e, node.id)}
    >
      {/* 悬浮标题：hover / 选中时显示在卡片上方，双击改名（对齐 infinite-canvas） */}
      {editingTitle ? (
        <div className="absolute left-4 top-[-28px] z-[70] max-w-[calc(100%-32px)]" data-no-drag>
          <input
            autoFocus
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitTitle();
              if (e.key === 'Escape') setEditingTitle(false);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className="w-full rounded-md border border-blue-400 bg-white px-1.5 py-0.5 text-xs font-semibold text-slate-700 outline-none"
            placeholder={visual.label}
            maxLength={40}
          />
        </div>
      ) : (
        <div
          className={`absolute left-4 top-[-22px] z-[70] max-w-[calc(100%-32px)] transition-opacity ${
            selected ? 'opacity-100' : 'pointer-events-none opacity-0 group-hover:opacity-100'
          }`}
        >
          <span
            className="block max-w-full cursor-text truncate border-b border-dashed border-transparent px-0 py-0.5 text-left text-xs font-medium text-slate-500 opacity-80 transition hover:border-current hover:opacity-100"
            title={`${nodeTitle}（双击重命名）`}
            onDoubleClick={(e) => {
              e.stopPropagation();
              startTitleEditing();
            }}
          >
            {nodeTitle}
          </span>
        </div>
      )}

      {/* 顶部抓手：卡片本体拖拽的保底把手（hover 浮现）。
          不加 data-no-drag：pointerdown 冒泡到卡片根由统一拖拽逻辑接管 */}
      <div
        className="absolute left-1/2 top-1 z-[55] h-1.5 w-10 -translate-x-1/2 cursor-grab rounded-full bg-slate-300 opacity-0 transition-opacity group-hover:opacity-70"
        title="拖动节点"
      />

      {/* 卡片内容（裁剪层：圆角内裁剪；无头部条，内容占满卡片）。ref 供高度自适应测量。
          stretchContent：内容层随卡片（minHeight）拉伸——手动放大卡片时把剩余高度
          交给节点内容的 flex-1 区域（如智能体指令输入框），自然高度行为不变 */}
      <div
        ref={contentRef}
        className={`overflow-hidden rounded-[22px] px-3 pb-3 pt-4 ${
          stretchContent ? 'flex min-h-0 flex-1 flex-col ' : ''
        }`}
      >
        {children}
      </div>

      {/* 悬浮工具栏：hover 浮现在卡片上方外侧，避免盖住节点内容（如文本输入区）。
          与卡片的视觉间距用外层容器的 padding 而非 margin 承载：margin 产生的间隙
          不属于任何元素，鼠标从卡片移向工具栏时会脱离 group 的 hover 链，工具栏在
          半路消失且 pointer-events 立即失效；padding 属于容器自身 hitbox，悬停链
          连续。选中态常显（与顶部抓手 / 节点标题行为一致），不依赖 hover。 */}
      <div
        className={`absolute bottom-full right-0 z-[60] max-w-full pb-1.5 transition-opacity lg:pointer-events-none lg:opacity-0 ${
          selected
            ? 'lg:pointer-events-auto lg:opacity-100'
            : 'lg:group-hover:pointer-events-auto lg:group-hover:opacity-100'
        }`}
        data-no-drag
      >
        <div className="flex max-w-full flex-wrap items-center justify-end gap-0.5 rounded-lg border border-slate-200/70 bg-white/95 px-1 py-0.5 opacity-95 shadow-sm backdrop-blur-sm">
          {toolbar}
          {toolbar && <span aria-hidden="true" className="mx-0.5 h-4 w-px bg-slate-200" />}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // 冻结单元守卫：节点属于智能体批次时确认整批删除（含外框），取消不删
              void deleteNodesWithUnitGuard([node.id]);
            }}
            className="grid h-6 w-6 place-items-center rounded-md text-slate-400 transition hover:bg-red-50 hover:text-red-500"
            title="删除节点"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* 左锚点（目标）：大热区 + 小圆点，hover 节点浮现 */}
      <div
        className="absolute -left-6 top-1/2 z-30 grid size-12 -translate-y-1/2 place-items-center opacity-0 transition-opacity duration-150 group-hover:opacity-100"
        title="输入锚点"
      >
        <div className="pointer-events-none size-3 rounded-full border-2 bg-white shadow-sm" style={{ borderColor: visual.accent }} />
      </div>

      {/* 右锚点（源，可拖拽建立连线）：大热区 + 小圆点 hover 放大 */}
      <div
        className="absolute -right-6 top-1/2 z-30 grid size-12 -translate-y-1/2 cursor-crosshair place-items-center opacity-0 transition-opacity duration-150 group-hover:opacity-100"
        title="拖到目标节点建立连接"
        onPointerDown={startConnection}
        data-no-drag
      >
        <div
          className="pointer-events-none size-3 rounded-full border-2 bg-white shadow-sm transition-transform hover:scale-125"
          style={{ borderColor: visual.accent }}
        />
      </div>

      {/* 四角 resize 手柄（拖动改变节点尺寸；hover 节点浮现） */}
      {([
        ['nw', '-left-[14px] -top-[14px] cursor-nwse-resize'],
        ['ne', '-right-[14px] -top-[14px] cursor-nesw-resize'],
        ['sw', '-bottom-[14px] -left-[14px] cursor-nesw-resize'],
        ['se', '-bottom-[14px] -right-[14px] cursor-nwse-resize'],
      ] as const).map(([corner, cls]) => (
        <div
          key={corner}
          className={`absolute z-50 size-7 opacity-0 transition-opacity group-hover:opacity-100 ${cls}`}
          data-no-drag
          onPointerDown={startResize(corner)}
          onPointerMove={onResizeMove}
          onPointerUp={endResize}
        />
      ))}

      {/* 右键菜单 */}
      {menuState && (
        <ContextMenu
          state={menuState}
          onClose={closeMenu}
          onInfo={() => {
            closeMenu();
            setInfoOpen(true);
          }}
        />
      )}

      {/* 节点信息弹窗 */}
      {infoOpen && <NodeInfoDialog node={node} onClose={() => setInfoOpen(false)} />}

      {/* 连接创建菜单 */}
      {connectionMenuState && (
        <ConnectionCreateMenu
          x={connectionMenuState.x}
          y={connectionMenuState.y}
          sourceNodeId={connectionMenuState.sourceNodeId}
          onClose={() => setConnectionMenuState(null)}
        />
      )}

      {/* 下方面板：选中时在卡片下方展开（提示词编辑等大块表单，随画布缩放） */}
      {panel && selected && (
        <div
          className="absolute left-1/2 top-full z-[80] w-[560px] max-w-[70vw] -translate-x-1/2 pt-4"
          data-no-drag
        >
          <div className="rounded-2xl border border-slate-200 bg-white p-3 text-xs shadow-xl shadow-slate-900/10">
            {panel}
          </div>
        </div>
      )}
    </div>
  );
}